import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { FormModal, FormSection } from '../components/FormModal.tsx';
import { IdList } from '../components/IdList.tsx';
import { SearchSelect } from '../components/SearchSelect.tsx';
import { Button, EmptyRow, Input, issueFor, Loading, Table } from '../components/ui.tsx';
import { usePeopleOptions } from './people/common.tsx';
import { jobChoices, jobLabel } from './people/people-choices.ts';

interface JobScope {
  families: { id: string; name: string | null }[];
  subfamilies: { id: string; name: string | null; familyName: string | null }[];
  exceptions: {
    jobId: string;
    code: string | null;
    name: string | null;
    justification: string;
  }[];
}

/**
 * Cargos do departamento (ADR 0096): por família, por subfamília e, como exceção justificada, por
 * cargo. A ligação só decide quais cargos aparecem primeiro na movimentação: não bloqueia nenhum e
 * não muda o catálogo.
 */
export function DepartmentJobScopeForm({
  department,
  onClose,
}: {
  department: { id: string; name: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const scope = useQuery({
    queryKey: ['departments', department.id, 'job-scope'],
    queryFn: () => api.get<JobScope>(`/core/departments/${department.id}/job-scope`),
  });
  return scope.data ? (
    <ScopeForm department={department} scope={scope.data} onClose={onClose} />
  ) : (
    <FormModal
      open
      title={t('departments.jobScope.title')}
      description={department.name}
      dirty={false}
      saving={false}
      error={scope.error}
      onSave={() => undefined}
      onClose={onClose}
    >
      <Loading />
    </FormModal>
  );
}

function ScopeForm({
  department,
  scope,
  onClose,
}: {
  department: { id: string; name: string };
  scope: JobScope;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const options = usePeopleOptions();
  const [familyIds, setFamilyIds] = useState(scope.families.map((f) => f.id));
  const [subfamilyIds, setSubfamilyIds] = useState(scope.subfamilies.map((s) => s.id));
  const [exceptions, setExceptions] = useState(scope.exceptions);
  const [adding, setAdding] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.put(`/core/departments/${department.id}/job-scope`, {
        familyIds,
        subfamilyIds,
        exceptions: exceptions.map(({ jobId, justification }) => ({ jobId, justification })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['departments', department.id, 'job-scope'] });
      await qc.invalidateQueries({ queryKey: ['people', 'options'] });
      onClose();
    },
  });
  const o = options.data;
  const familyName = (id: string) => o?.families.find((f) => f.id === id)?.name ?? '';
  const addJob = (jobId: string) => {
    const job = o?.jobs.find((j) => j.id === jobId);
    setExceptions([
      ...exceptions,
      { jobId, code: job?.code ?? null, name: job?.name ?? null, justification: '' },
    ]);
    setAdding('');
  };
  const jobName = (e: JobScope['exceptions'][number]) => {
    const job = o?.jobs.find((j) => j.id === e.jobId);
    return job ? jobLabel(job) : `${e.code ?? ''} · ${e.name ?? ''}`;
  };
  return (
    <FormModal
      open
      size="xl"
      title={t('departments.jobScope.title')}
      description={department.name}
      dirty
      saving={save.isPending}
      error={save.error}
      onSave={() => save.mutate()}
      onClose={onClose}
    >
      <p className="text-sm text-muted">{t('departments.jobScope.hint')}</p>
      <FormSection columns={1}>
        <IdList
          label={t('departments.jobScope.families')}
          addLabel={t('departments.jobScope.addFamily')}
          value={familyIds}
          onChange={setFamilyIds}
          items={[
            ...(o?.families ?? []).map((f) => ({ id: f.id, label: f.name })),
            ...scope.families
              .filter((f) => !o?.families.some((x) => x.id === f.id))
              .map((f) => ({ id: f.id, label: f.name ?? f.id })),
          ]}
        />
        <IdList
          label={t('departments.jobScope.subfamilies')}
          addLabel={t('departments.jobScope.addSubfamily')}
          value={subfamilyIds}
          onChange={setSubfamilyIds}
          items={[
            ...(o?.subfamilies ?? []).map((s) => ({
              id: s.id,
              label: `${familyName(s.familyId)} · ${s.name}`,
            })),
            ...scope.subfamilies
              .filter((s) => !o?.subfamilies.some((x) => x.id === s.id))
              .map((s) => ({ id: s.id, label: `${s.familyName ?? ''} · ${s.name ?? s.id}` })),
          ]}
        />
      </FormSection>
      <FormSection title={t('departments.jobScope.exceptions')} columns={1}>
        <p className="text-xs text-muted">{t('departments.jobScope.exceptionsHint')}</p>
        <Table head={[t('departments.jobScope.job'), t('departments.jobScope.justification'), '']}>
          {exceptions.length === 0 && (
            <EmptyRow colSpan={3}>{t('departments.jobScope.noExceptions')}</EmptyRow>
          )}
          {exceptions.map((e) => (
            <tr key={e.jobId}>
              <td>{jobName(e)}</td>
              <td>
                <Input
                  required
                  maxLength={500}
                  aria-label={t('departments.jobScope.justification')}
                  value={e.justification}
                  onChange={(ev) =>
                    setExceptions(
                      exceptions.map((x) =>
                        x.jobId === e.jobId ? { ...x, justification: ev.target.value } : x,
                      ),
                    )
                  }
                />
              </td>
              <td className="text-right">
                <Button
                  variant="secondary"
                  onClick={() => setExceptions(exceptions.filter((x) => x.jobId !== e.jobId))}
                >
                  {t('common.remove')}
                </Button>
              </td>
            </tr>
          ))}
        </Table>
        {issueFor(save.error, 'exceptions') && (
          <p className="text-xs text-danger">{issueFor(save.error, 'exceptions')}</p>
        )}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('departments.jobScope.addException')}</span>
          <SearchSelect
            value={adding}
            onChange={addJob}
            placeholder={t('departments.jobScope.searchJob')}
            options={
              o
                ? jobChoices(
                    { ...o, jobs: o.jobs.filter((j) => j.status === 'active') },
                    { id: null, label: null },
                    {
                      current: t('people.movement.current'),
                      others: t('people.movement.others'),
                      inactive: t('people.movement.inactive'),
                      compatible: t('people.movement.compatibleJobs'),
                      otherJobs: t('people.movement.otherJobs'),
                      outside: t('people.movement.outsideDepartment'),
                    },
                  ).filter((c) => !exceptions.some((e) => e.jobId === c.id))
                : []
            }
          />
        </label>
      </FormSection>
    </FormModal>
  );
}
